import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { classNames } from './helpers';

const AttachmentActions = ({ attachment, onEdit, onDelete, minimal = false, className, confirmPrompt = 'Confirm delete?' }) => {
  const previewUrl = attachment.links?.download || attachment.links?.direct;
  const publicUrl = attachment.links?.direct;
  const cachedUrl = attachment.links?.cached;
  const wrapperRef = useRef(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperClass = classNames(
    minimal ? 'mt-2' : 'pt-2 mt-3',
    className
  );

  useEffect(() => {
    setConfirmingDelete(false);
    setMenuOpen(false);
  }, [attachment?.id]);

  useEffect(() => {
    const handleDocClick = (e) => {
      if (!menuOpen) return;
      if (wrapperRef.current && wrapperRef.current.contains(e.target)) return;
      setMenuOpen(false);
      setConfirmingDelete(false);
    };
    document.addEventListener('click', handleDocClick);
    return () => document.removeEventListener('click', handleDocClick);
  }, [menuOpen]);

  const triggerDeleteConfirmation = () => setConfirmingDelete(true);
  const cancelDeleteConfirmation = () => setConfirmingDelete(false);
  const confirmDelete = () => {
    setConfirmingDelete(false);
    setMenuOpen(false);
    onDelete(attachment);
  };

  return (
    <div className={wrapperClass} ref={wrapperRef}>
      <div className={classNames('dropdown', menuOpen ? 'open' : '')} style={{ display: 'inline-block' }}>
        <button
          type="button"
          className="btn btn-default btn-xs dropdown-toggle"
          aria-haspopup="true"
          aria-expanded={menuOpen}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMenuOpen((v) => !v);
            if (menuOpen) setConfirmingDelete(false);
          }}
        >
          Actions <span className="caret" />
        </button>

        {menuOpen ? (
          <ul className="dropdown-menu dropdown-menu-right" style={{ display: 'block' }}>
            {previewUrl ? (
              <li>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                  }}
                >
                  <i className="fa fa-eye" style={{ width: 16 }} aria-hidden="true" /> Preview
                </a>
              </li>
            ) : null}
            {publicUrl ? (
              <li>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                  }}
                >
                  <i className="fa fa-link" style={{ width: 16 }} aria-hidden="true" />
                  {attachment.is_private ? ' Private' : ' Public'}
                </a>
              </li>
            ) : null}
            {cachedUrl ? (
              <li>
                <a
                  href={cachedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                  }}
                >
                  <i className="fa fa-link" style={{ width: 16 }} aria-hidden="true" /> Cached
                </a>
              </li>
            ) : null}

            {(previewUrl || publicUrl || cachedUrl) ? <li role="separator" className="divider" /> : null}

            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenuOpen(false);
                  onEdit(attachment);
                }}
              >
                <i className="fa fa-pencil" style={{ width: 16 }} aria-hidden="true" /> Edit
              </a>
            </li>

            {!confirmingDelete ? (
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    triggerDeleteConfirmation();
                  }}
                  style={{ color: '#a94442' }}
                >
                  <i className="fa fa-trash" style={{ width: 16 }} aria-hidden="true" /> Delete
                </a>
              </li>
            ) : (
              <li style={{ padding: '3px 10px' }}>
                <div className="clearfix">
                  <span className="text-muted" style={{ fontSize: 12, paddingTop: 4, display: 'inline-block' }}>
                    {confirmPrompt}
                  </span>
                  <span className="pull-right btn-group btn-group-xs">
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        confirmDelete();
                      }}
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        cancelDeleteConfirmation();
                      }}
                    >
                      Cancel
                    </button>
                  </span>
                </div>
              </li>
            )}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

AttachmentActions.propTypes = {
  attachment: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  minimal: PropTypes.bool,
  className: PropTypes.string,
  confirmPrompt: PropTypes.string,
};

AttachmentActions.defaultProps = {
  minimal: false,
  className: undefined,
  confirmPrompt: 'Confirm delete?',
};

export default AttachmentActions;
